import { useCallback, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Login from "./components/Login";
import { BODY_THEME_KEY, DARK_THEME, DarkOrLightType, LIGHT_THEME, THEME_KEY } from "./common/constants";
import { Navbar } from "./components/Navbar";
import Register from "./components/Register";
import Footer from "./components/Footer";
import { AuthProvider } from "./providers/AuthContext";
import Divider from "./components/Divider";
import { AppRoutes } from "./common/app-routes";
import { NavSections } from "./common/nav-sections";
import ScrollToTop from "./components/ScrollToTop";
import PaymentSuccess from "./components/PaymentSuccess";
import { Toaster } from "react-hot-toast"
import PaymentFailed from "./components/PaymentFailed";
import NotFound from "./components/NotFound";
import LandingPage from "./components/LandingPage";
import TermsAndConditions from "./components/TermsAndConditions";
import PrivacyPolicy from "./components/PrivacyPolicy";
import LicenseAgreement from "./components/LicenseAgreement";
import FloatingLightbulb from "./components/FloatingLightbulb";
import ErrorBoundary from "./components/ErrorBoundary";
import UnexpectedErrorPage from "./components/UnexpectedErrorPage";
import ApiSecurityPanel from "./components/ApiSecurityPanel";
import UsageWidget from "./components/UsageWidget";
import { UsageProvider } from "./providers/UsageProvider";
import Jobs from "./components/Jobs";
import { JobsProvider } from "./providers/JobsProvider";
import ApiDocsPage from "./components/ApiDocsPage";

const App = () => {
  const [theme, setTheme] = useState<DarkOrLightType>(() => {
    const savedTheme = localStorage.getItem(THEME_KEY);
    return savedTheme === LIGHT_THEME ? savedTheme : DARK_THEME;
  });

  const toggleTheme = () => {
    setTheme((prev) => (prev === LIGHT_THEME ? DARK_THEME : LIGHT_THEME));
  };

  const [activeSection, setActiveSection] = useState(NavSections.WELCOME.toString());
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => setMenuOpen((prev) => !prev), [setMenuOpen]);

  const changeActiveSection = useCallback((id: string) => {
    setMenuOpen(false);
    setActiveSection(id);
  }, [setMenuOpen, setActiveSection]);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
    document.body.setAttribute(BODY_THEME_KEY, theme);
  }, [theme]);

  return (
    <ErrorBoundary fallback={<UnexpectedErrorPage />}>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}>
        <AuthProvider>
          <JobsProvider>
          <UsageProvider>
            <Toaster
              position={'bottom-left'} reverseOrder={false}
              toastOptions={
                {
                  style: {
                    maxWidth: '90vw',
                    fontSize: '1rem',
                    background: 'var(--toast-bg)',
                    color: 'var(--toast-text)',
                  },
                  success: {
                    iconTheme: {
                      primary: '#22c55e',
                      secondary: '#fff',
                    },
                  },
                }
              }
            />
            <Router>
              <ScrollToTop />
              <Navbar activeSection={activeSection} changeActiveSection={changeActiveSection} menuOpen={menuOpen} setActiveSection={setActiveSection} toggleMenu={toggleMenu} />
              <UsageWidget />
              <FloatingLightbulb isDark={theme === DARK_THEME} toggleTheme={toggleTheme} />
              <Routes>
                <Route path={AppRoutes.HOME} element={<LandingPage changeActiveSection={changeActiveSection} />} />
                <Route path={AppRoutes.LOGIN} element={<Login changeActiveSection={changeActiveSection} theme={theme} />} />
                <Route path={AppRoutes.REGISTER} element={<Register changeActiveSection={changeActiveSection} theme={theme} />} />
                <Route path={AppRoutes.PAYMENT_SUCCESS} element={<PaymentSuccess />} />
                <Route path={AppRoutes.PAYMENT_FAILED} element={<PaymentFailed changeActiveSection={changeActiveSection} />} />
                <Route path={AppRoutes.TERMS_AND_CONDITIONS} element={<TermsAndConditions />} />
                <Route path={AppRoutes.PRIVACY_POLICY} element={<PrivacyPolicy />} />
                <Route path={AppRoutes.LICENSE} element={<LicenseAgreement />} />
                <Route path={AppRoutes.NOT_FOUND} element={<NotFound />} />
                <Route path={AppRoutes.JOBS} element={<Jobs/>} />
                <Route path={AppRoutes.MANAGE_API} element={<ApiSecurityPanel />} />
                <Route path={AppRoutes.DOCS} element={<ApiDocsPage />} />
              </Routes>
              <Divider />
              <Footer />
            </Router>
          </UsageProvider>
          </JobsProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
};

export default App;